/**
 * Browser config: prefer window.__RUNTIME_CONFIG__ (written at container start from env /
 * Kubernetes secrets), then build-time env from .env* (Vite `define` inlines static process.env.* only).
 *
 * Note: any value here is visible in the browser — use only for public client config.
 */

/**
 * Static process.env.* keys — Vite replaces these at build/dev time. Do not use process.env[key] dynamically.
 */
const BUILD_ENV = {
  REACT_APP_MAIN_HTTP_API_URL: process.env.REACT_APP_MAIN_HTTP_API_URL,
  REACT_APP_MAIN_SOCKET_API_URL: process.env.REACT_APP_MAIN_SOCKET_API_URL,
  REACT_APP_AUTH0_DOMAIN: process.env.REACT_APP_AUTH0_DOMAIN,
  REACT_APP_AUTH0_CLIENT_ID: process.env.REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_GOOGLE_MAPS_API_KEY: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  REACT_APP_GOOGLE_MAPS_MAP_ID: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID,
  REACT_APP_DEFAULT_MAP_CENTER: process.env.REACT_APP_DEFAULT_MAP_CENTER,
  REACT_APP_DEFAULT_MAP_ZOOM_LEVEL: process.env.REACT_APP_DEFAULT_MAP_ZOOM_LEVEL,
  REACT_APP_SOCKET_URL: process.env.REACT_APP_SOCKET_URL,
  REACT_APP_USE_MARKER_CLUSTERER: process.env.REACT_APP_USE_MARKER_CLUSTERER,
  REACT_APP_I18N_SAVE_MISSING: process.env.REACT_APP_I18N_SAVE_MISSING,
  REACT_APP_DISABLE_2FA: process.env.REACT_APP_DISABLE_2FA,
  REACT_APP_VAPID_PUBLIC_KEY: process.env.REACT_APP_VAPID_PUBLIC_KEY
}

function readRuntime () {
  if (typeof window === 'undefined') return {}
  return window.__RUNTIME_CONFIG__ || {}
}

function readBuildEnv (key) {
  if (!Object.prototype.hasOwnProperty.call(BUILD_ENV, key)) return undefined
  const v = BUILD_ENV[key]
  if (v === undefined || v === null) return undefined
  const s = String(v).trim()
  return s === '' ? undefined : s
}

export function getRuntimeEnv (key) {
  const rt = readRuntime()
  if (Object.prototype.hasOwnProperty.call(rt, key)) {
    const v = rt[key]
    if (v === undefined || v === null) return readBuildEnv(key)
    const s = String(v).trim()
    // docker-entrypoint writes "" for unset K8s env vars — treat as missing so build-time .env values still apply
    if (s === '') return readBuildEnv(key)
    return s
  }
  return readBuildEnv(key)
}

export const REACT_APP_MAIN_HTTP_API_URL = getRuntimeEnv('REACT_APP_MAIN_HTTP_API_URL')
export const REACT_APP_MAIN_SOCKET_API_URL = getRuntimeEnv('REACT_APP_MAIN_SOCKET_API_URL')
export const REACT_APP_AUTH0_DOMAIN = getRuntimeEnv('REACT_APP_AUTH0_DOMAIN')
export const REACT_APP_AUTH0_CLIENT_ID = getRuntimeEnv('REACT_APP_AUTH0_CLIENT_ID')
export const REACT_APP_GOOGLE_MAPS_API_KEY = getRuntimeEnv('REACT_APP_GOOGLE_MAPS_API_KEY')
/** Vector map ID (Google Cloud Console) — required for AdvancedMarkerElement. Override per deployment. */
export const REACT_APP_GOOGLE_MAPS_MAP_ID = getRuntimeEnv('REACT_APP_GOOGLE_MAPS_MAP_ID')
export const REACT_APP_DEFAULT_MAP_CENTER = getRuntimeEnv('REACT_APP_DEFAULT_MAP_CENTER')
export const REACT_APP_DEFAULT_MAP_ZOOM_LEVEL = getRuntimeEnv('REACT_APP_DEFAULT_MAP_ZOOM_LEVEL')
export const REACT_APP_SOCKET_URL = getRuntimeEnv('REACT_APP_SOCKET_URL')
export const REACT_APP_USE_MARKER_CLUSTERER = getRuntimeEnv('REACT_APP_USE_MARKER_CLUSTERER')

/**
 * Predicate used by {@link isMarkerClusteringEnabled} (exported for unit tests).
 * @param {string|undefined|null} v - raw `REACT_APP_USE_MARKER_CLUSTERER` from env or runtime config
 */
export function isMarkerClusteringEnabledFromRawValue (v) {
  if (v === undefined || v === null) return true
  const s = String(v).trim()
  if (s === '') return true
  return s.toLowerCase() !== 'false'
}

/**
 * Admin map device / tracking-group markers use MarkerClusterer (@react-google-maps/api).
 * Default is on for performance with many markers. Set `REACT_APP_USE_MARKER_CLUSTERER` to the string `false` (runtime or build env) to disable.
 */
export function isMarkerClusteringEnabled () {
  return isMarkerClusteringEnabledFromRawValue(REACT_APP_USE_MARKER_CLUSTERER)
}

export const REACT_APP_I18N_SAVE_MISSING = getRuntimeEnv('REACT_APP_I18N_SAVE_MISSING')
export const REACT_APP_DISABLE_2FA = getRuntimeEnv('REACT_APP_DISABLE_2FA')
/** VAPID public key for Web Push (must match socket service VAPID_PRIVATE_KEY pair). */
export const REACT_APP_VAPID_PUBLIC_KEY = getRuntimeEnv('REACT_APP_VAPID_PUBLIC_KEY')

/**
 * @param {string|undefined|null} v - raw `REACT_APP_DISABLE_2FA` from env or runtime config
 */
export function isTwoFactorAuthDisabledFromRawValue (v) {
  if (v === undefined || v === null) return false
  return String(v).trim().toLowerCase() === 'true'
}

/**
 * Skip email auth-code step after password login (requires API Auth:DisableTwoFactor).
 * Set REACT_APP_DISABLE_2FA to the string "true" for local dev.
 */
export function isTwoFactorAuthDisabled () {
  return isTwoFactorAuthDisabledFromRawValue(REACT_APP_DISABLE_2FA)
}
